// focusTimeline — pure helpers for the "Today's blocks" timeline strip
// on the Focus page. Renders today's active focus_rules as positioned
// bars against a 6 AM → 10 PM (16h) horizontal ruler with a "now"
// indicator line.
//
// Why a separate helper file (mirrors utils/focusActive.ts): tests can
// pump in fixed `now` + tz pairs without mounting React, and the
// rendering component stays small.
//
// Window semantics: blocks are clipped to [WINDOW_START_HOUR,
// WINDOW_END_HOUR]. A rule with start before 6 AM (e.g. 05:00 → 07:00)
// renders from 0% to ((7-6)/16)*100% = 6.25% and carries the
// `startsBeforeWindow` flag so the UI can draw a "ragged left edge"
// to indicate truncation. Same logic at the right edge.
//
// Timezone: rendered in `displayTz` (caller supplies — typically the
// browser's resolved IANA, or for a multi-tz future, the user's
// "primary work tz" setting). All seed rules today use PST so the
// browser-tz default works for the dev environment.

export type TimelineRule = {
  id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  timezone: string | null;
  label: string;
  is_active: boolean;
};

export type TimelineBlock = {
  rule: TimelineRule;
  leftPct: number; // 0..100, percent from window start
  widthPct: number; // 0..100, percent of window
  startsBeforeWindow: boolean;
  endsAfterWindow: boolean;
  isActive: boolean;
};

export const WINDOW_START_HOUR = 6;
export const WINDOW_END_HOUR = 22;
export const WINDOW_HOURS = WINDOW_END_HOUR - WINDOW_START_HOUR; // 16

// Hour-tick marks rendered on the ruler. Picked at 3-hour stride so
// six labels fit comfortably without crowding on the 1-col layout.
export const TIMELINE_TICK_HOURS = [6, 9, 12, 15, 18, 21];

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

function parseHmsToHours(hms: string): number {
  const parts = hms.split(':').map(Number);
  const h = parts[0] ?? 0;
  const m = parts[1] ?? 0;
  const s = parts[2] ?? 0;
  return h + m / 60 + s / 3600;
}

function getDayOfWeek(now: Date, tz: string): number {
  try {
    const wd = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(now);
    return WEEKDAY_INDEX[wd] ?? 0;
  } catch {
    return now.getDay();
  }
}

function getHourFraction(now: Date, tz: string): number {
  // Returns hours-of-day in tz as a float. e.g. 08:30:15 → 8.504166...
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(now);
    let h = 0;
    let m = 0;
    let s = 0;
    for (const p of parts) {
      if (p.type === 'hour') h = Number(p.value);
      else if (p.type === 'minute') m = Number(p.value);
      else if (p.type === 'second') s = Number(p.value);
    }
    // Some Chrome legacy emits 24 for midnight in hour12:false; normalize.
    if (h === 24) h = 0;
    return h + m / 60 + s / 3600;
  } catch {
    return now.getHours() + now.getMinutes() / 60 + now.getSeconds() / 3600;
  }
}

/**
 * Format an hour-tick label for the ruler.
 * 6 → "6a", 12 → "12p", 15 → "3p", 21 → "9p"
 */
export function formatHourTick(hour: number): string {
  if (hour === 0) return '12a';
  if (hour === 12) return '12p';
  return hour < 12 ? `${hour}a` : `${hour - 12}p`;
}

/**
 * Given the set of focus_rules and a clock instant in `displayTz`,
 * return the blocks to render in today's timeline (filtered to
 * is_active=true, today's DOW, intersecting the [WINDOW_START_HOUR,
 * WINDOW_END_HOUR] window), the percent position of "now", and the
 * id of whichever block contains "now" (null if none).
 */
export function getTodayTimelineBlocks(
  rules: TimelineRule[],
  now: Date,
  displayTz: string,
): { blocks: TimelineBlock[]; nowPct: number; activeBlockId: string | null } {
  const todayDow = getDayOfWeek(now, displayTz);
  const nowHour = getHourFraction(now, displayTz);

  const blocks: TimelineBlock[] = [];
  let activeBlockId: string | null = null;

  for (const rule of rules) {
    if (!rule.is_active) continue;
    if (rule.day_of_week !== todayDow) continue;

    const startH = parseHmsToHours(rule.start_time);
    const endH = parseHmsToHours(rule.end_time);

    // Drop blocks entirely outside the visible window.
    if (endH <= WINDOW_START_HOUR || startH >= WINDOW_END_HOUR) continue;

    const clampedStart = Math.max(startH, WINDOW_START_HOUR);
    const clampedEnd = Math.min(endH, WINDOW_END_HOUR);

    const leftPct = ((clampedStart - WINDOW_START_HOUR) / WINDOW_HOURS) * 100;
    const widthPct = ((clampedEnd - clampedStart) / WINDOW_HOURS) * 100;

    const isActive = nowHour >= startH && nowHour < endH;
    // First-match-wins for the activeBlockId. Mirrors
    // findActiveFocusRule's behavior so the same rule "wins" in the
    // PageStatsRow tile and the timeline. All overlapping rules still
    // get isActive=true so the UI can highlight them consistently.
    if (isActive && activeBlockId === null) activeBlockId = rule.id;

    blocks.push({
      rule,
      leftPct,
      widthPct,
      startsBeforeWindow: startH < WINDOW_START_HOUR,
      endsAfterWindow: endH > WINDOW_END_HOUR,
      isActive,
    });
  }

  // Clamp nowPct to [-Infinity, Infinity] semantically — the caller
  // decides whether to render the indicator. We return the raw value
  // so the UI can hide the line when "now" is outside the window
  // (e.g. someone scoping the page at 4 AM).
  const nowPct = ((nowHour - WINDOW_START_HOUR) / WINDOW_HOURS) * 100;

  // Sort blocks by leftPct so overlapping bars stack predictably and
  // the labels render in time order.
  blocks.sort((a, b) => a.leftPct - b.leftPct);

  return { blocks, nowPct, activeBlockId };
}
